import type { AnyFieldApi } from "@tanstack/react-form";

import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils/cn";
import { firstFieldError } from "@/components/form/validators";

interface TextareaFieldProps {
  field: AnyFieldApi;
  label: string;
  placeholder?: string;
  rows?: number;
  className?: string;
}

/**
 * Multiline counterpart to the form kit's TextField, bound to a TanStack Form
 * field so the action-plan autosave forms use the same accessible label/error
 * wiring as the rest of the app.
 */
export function TextareaField({
  field,
  label,
  placeholder,
  rows = 3,
  className,
}: TextareaFieldProps) {
  const error = firstFieldError(field);
  const errorId = `${field.name}-error`;
  const invalid = error !== undefined;

  return (
    <div className={cn("space-y-1.5", className)}>
      <Label htmlFor={field.name}>{label}</Label>
      <textarea
        id={field.name}
        name={field.name}
        rows={rows}
        placeholder={placeholder}
        value={(field.state.value as string | undefined) ?? ""}
        onChange={(e) => field.handleChange(e.target.value)}
        onBlur={field.handleBlur}
        aria-invalid={invalid}
        aria-describedby={invalid ? errorId : undefined}
        className={cn(
          "flex w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
          "aria-[invalid=true]:border-destructive",
        )}
      />
      {invalid && (
        <p id={errorId} className="text-sm font-medium text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}
